/**
 * La catena, disegnata (F-096, condivisa da F-105).
 *
 * Sta in un modulo suo perché **due pagine devono mostrare la stessa cosa**:
 * la riconciliazione di un'operazione, che risale, e quella di un resoconto,
 * che scende. La richiesta era esplicita — «utilizza la stessa pagina e
 * visualizzazione» — e due copie che si somigliano avrebbero cominciato a
 * divergere alla prima modifica, lasciando lo stesso fatto raccontato in due
 * modi a seconda di dove lo si guarda.
 *
 * Due scelte che vengono da qui e valgono per entrambe:
 *
 *  1. **Gli anelli mancanti si mostrano.** Un passo che non è avvenuto è
 *     informazione — dice dove la catena si è interrotta — ed è spesso la
 *     ragione per cui si apre la pagina. Nasconderlo lascerebbe un diagramma
 *     più pulito e una domanda senza risposta.
 *  2. **Una colonna, non un grafo.** La catena *è* lineare, e disegnarla come
 *     tale è più leggibile di un grafico che finge una complessità che non c'è.
 */

import { Link } from 'react-router-dom';
import { formatDateTime } from '@/lib/format';
import type { TraceStep } from './api';

const STEP_ICON: Record<TraceStep['kind'], string> = {
  trigger: '⚡',
  daily_briefing: '🗞️',
  briefing_item: '📌',
  instrument_analysis: '🔬',
  wishlist_item: '✅',
  proposal: '💡',
  llm_check: '🤖',
  broker_order: '📤',
  transaction: '📒',
  agent_run: '🤖',
};

function FlowStep({ step, last }: { step: TraceStep; last: boolean }) {
  const body = (
    <>
      <span className="trace__icon" aria-hidden="true">
        {STEP_ICON[step.kind]}
      </span>
      <span className="trace__body">
        <span className="trace__label">
          {step.label}
          {step.missing && <span className="trace__missing"> · non avvenuto</span>}
        </span>
        {step.detail && <span className="trace__detail">{step.detail}</span>}
        {step.at && <span className="trace__at">{formatDateTime(step.at)}</span>}
      </span>
    </>
  );

  return (
    <li className={`trace__step ${step.missing ? 'trace__step--missing' : ''} ${last ? 'trace__step--last' : ''}`}>
      {step.href && !step.missing ? (
        <Link to={step.href} className="trace__row trace__row--link">
          {body}
        </Link>
      ) : (
        <span className="trace__row">{body}</span>
      )}
    </li>
  );
}

export function TraceFlow({ steps }: { steps: TraceStep[] }) {
  return (
    <ol className="trace__flow">
      {steps.map((step, index) => (
        // La chiave non è `step.kind`: la riconciliazione di un resoconto può
        // avere PIÙ passi dello stesso genere — due revisioni di digest, due
        // valutazioni — e una chiave ripetuta farebbe collassare righe diverse
        // in una sola agli occhi di React.
        <FlowStep key={`${step.kind}-${index}`} step={step} last={index === steps.length - 1} />
      ))}
    </ol>
  );
}
